import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ticket, TrendingUp, Users, DollarSign, Clock, CheckCircle2, XCircle, Loader2, Eye, QrCode, Copy, Check, ExternalLink, Link2, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface TicketOrder {
  id: string;
  buyer_id: string;
  status: string;
  total_amount: number;
  service_fee: number;
  commission_fee: number;
  stripe_fee: number;
  organizer_payout: number;
  currency: string;
  buyer_name: string;
  buyer_email: string;
  created_at: string;
  items?: {
    tier_name: string;
    quantity: number;
    unit_price: number;
  }[];
}

interface StockInfo {
  tier_name: string;
  total_quantity: number;
  sold_quantity: number;
}

function getCurrencySymbol(currency?: string) {
  switch (currency?.toUpperCase()) {
    case 'GBP': return '£';
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'CHF': return 'CHF ';
    default: return '€';
  }
}

interface CheckInToken {
  id: string;
  token: string;
  label: string | null;
  expires_at: string;
  is_active: boolean;
  created_at: string;
}

export function TicketSalesOverview({ eventId, currency, eventEndDate }: { eventId: string; currency?: string; eventEndDate?: string }) {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<TicketOrder[]>([]);
  const [stock, setStock] = useState<StockInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOrders, setShowOrders] = useState(false);
  const [checkInTokens, setCheckInTokens] = useState<CheckInToken[]>([]);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const sym = getCurrencySymbol(currency);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch orders for this event
        const { data: ordersData } = await supabase
          .from('ticket_orders')
          .select('*')
          .eq('event_id', eventId)
          .order('created_at', { ascending: false });

        if (ordersData && ordersData.length > 0) {
          // Fetch items for all orders
          const orderIds = ordersData.map(o => o.id);
          const { data: itemsData } = await supabase
            .from('ticket_items')
            .select('*')
            .in('order_id', orderIds);

          const itemsMap = new Map<string, typeof itemsData>();
          itemsData?.forEach(item => {
            const existing = itemsMap.get(item.order_id) || [];
            existing.push(item);
            itemsMap.set(item.order_id, existing);
          });

          setOrders(ordersData.map(order => ({
            ...order,
            items: itemsMap.get(order.id) || [],
          })));
        }

        // Fetch stock
        const { data: stockData } = await supabase
          .from('ticket_tier_stock')
          .select('tier_name, total_quantity, sold_quantity')
          .eq('event_id', eventId);

        if (stockData) setStock(stockData);

        // Fetch existing check-in tokens
        const { data: tokensData } = await supabase
          .from('check_in_tokens')
          .select('*')
          .eq('event_id', eventId)
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (tokensData) setCheckInTokens(tokensData);
      } catch (err) {
        console.error('Error fetching ticket sales:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eventId]);

  /* ─── Generate a new check-in link ─────────────────────────────────── */
  const generateCheckInLink = async () => {
    setGeneratingLink(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Expiry: 24 hours after event end, or 48 hours from now (whichever is later)
      const fallbackExpiry = new Date(Date.now() + 48 * 60 * 60 * 1000);
      let expiry = fallbackExpiry;
      if (eventEndDate) {
        const eventEnd = new Date(eventEndDate);
        const afterEvent = new Date(eventEnd.getTime() + 24 * 60 * 60 * 1000);
        expiry = afterEvent > fallbackExpiry ? afterEvent : fallbackExpiry;
      }

      const { data, error } = await supabase
        .from('check_in_tokens')
        .insert({
          event_id: eventId,
          created_by: user.id,
          expires_at: expiry.toISOString(),
          label: checkInTokens.length === 0 ? 'Main Entrance' : `Door ${checkInTokens.length + 1}`,
        })
        .select()
        .single();

      if (error) throw error;
      setCheckInTokens(prev => [data, ...prev]);
    } catch (err) {
      console.error('Error generating check-in link:', err);
    } finally {
      setGeneratingLink(false);
    }
  };

  /* ─── Copy link to clipboard ───────────────────────────────────────── */
  const copyLink = async (token: CheckInToken) => {
    const url = `${window.location.origin}/check-in/${token.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedTokenId(token.id);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedTokenId(token.id);
      setTimeout(() => setCopiedTokenId(null), 2000);
    }
  };

  /* ─── Deactivate a token ───────────────────────────────────────────── */
  const deactivateToken = async (tokenId: string) => {
    try {
      await supabase
        .from('check_in_tokens')
        .update({ is_active: false })
        .eq('id', tokenId);
      setCheckInTokens(prev => prev.filter(t => t.id !== tokenId));
    } catch (err) {
      console.error('Error deactivating token:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-accent-text" />
      </div>
    );
  }

  const completedOrders = orders.filter(o => o.status === 'paid');
  const totalRevenue = completedOrders.reduce((sum, o) => sum + o.organizer_payout, 0);
  const totalTicketsSold = stock.reduce((sum, s) => sum + s.sold_quantity, 0);
  const totalCapacity = stock.reduce((sum, s) => sum + s.total_quantity, 0);
  const soldPercentage = totalCapacity > 0 ? Math.round((totalTicketsSold / totalCapacity) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-3.5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-accent-text/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-3.5 w-3.5 text-accent-text" />
            </div>
            <span className="text-[10px] text-content/40 uppercase font-medium">Revenue</span>
          </div>
          <p className="text-lg font-bold text-content">{sym}{(totalRevenue / 100).toFixed(2)}</p>
          <p className="text-[10px] text-content/30 mt-0.5">Your payout</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-3.5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
              <Ticket className="h-3.5 w-3.5 text-blue-500" />
            </div>
            <span className="text-[10px] text-content/40 uppercase font-medium">Sold</span>
          </div>
          <p className="text-lg font-bold text-content">{totalTicketsSold}</p>
          <p className="text-[10px] text-content/30 mt-0.5">of {totalCapacity} total</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-3.5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
            </div>
            <span className="text-[10px] text-content/40 uppercase font-medium">Orders</span>
          </div>
          <p className="text-lg font-bold text-content">{completedOrders.length}</p>
          <p className="text-[10px] text-content/30 mt-0.5">confirmed</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-3.5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-purple-50 rounded-lg flex items-center justify-center">
              <Users className="h-3.5 w-3.5 text-purple-500" />
            </div>
            <span className="text-[10px] text-content/40 uppercase font-medium">Fill Rate</span>
          </div>
          <p className="text-lg font-bold text-content">{soldPercentage}%</p>
          <div className="w-full bg-gray-100 rounded-full h-1 mt-1.5">
            <div
              className="bg-accent-text h-1 rounded-full transition-all"
              style={{ width: `${soldPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tier Breakdown */}
      {stock.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h4 className="text-sm font-semibold text-content mb-3">Ticket Tiers</h4>
          <div className="space-y-2.5">
            {stock.map(tier => {
              const pct = tier.total_quantity > 0 ? Math.round((tier.sold_quantity / tier.total_quantity) * 100) : 0;
              return (
                <div key={tier.tier_name} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-content truncate">{tier.tier_name}</span>
                      <span className="text-xs text-content/40 shrink-0">
                        {tier.sold_quantity} / {tier.total_quantity}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-accent-text h-1.5 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Check-in Management */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <button
          onClick={() => setShowCheckIn(!showCheckIn)}
          className="w-full flex items-center justify-between p-4"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-accent-text/10 rounded-lg flex items-center justify-center">
              <QrCode className="h-3.5 w-3.5 text-accent-text" />
            </div>
            <span className="text-sm font-semibold text-content">
              Check-in Scanner
            </span>
          </div>
          <span className={`text-xs font-medium px-2 py-1 rounded-lg transition-colors ${
            showCheckIn ? 'bg-accent-text/10 text-accent-text' : 'bg-gray-100 text-content/40'
          }`}>
            {checkInTokens.length > 0 ? `${checkInTokens.length} link${checkInTokens.length > 1 ? 's' : ''}` : 'Setup'}
          </span>
        </button>

        {showCheckIn && (
          <div className="border-t border-gray-100 p-4 space-y-3">
            <p className="text-xs text-content/45 leading-relaxed">
              Generate a shareable check-in link for your door staff. Anyone with the link can scan ticket QR codes — no account needed.
            </p>

            {/* Existing tokens */}
            {checkInTokens.map(token => {
              const url = `${window.location.origin}/check-in/${token.token}`;
              const isCopied = copiedTokenId === token.id;
              return (
                <div key={token.id} className="bg-gray-50 rounded-xl p-3.5 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link2 className="h-3.5 w-3.5 text-accent-text shrink-0" />
                      <span className="text-sm font-medium text-content truncate">
                        {token.label || 'Check-in Link'}
                      </span>
                    </div>
                    <button
                      onClick={() => deactivateToken(token.id)}
                      className="p-1.5 text-content/30 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Deactivate link"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0">
                      <input
                        readOnly
                        value={url}
                        className="w-full px-3 py-2 text-xs text-content/60 bg-white border border-gray-200 rounded-lg font-mono truncate"
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                    </div>
                    <button
                      onClick={() => copyLink(token)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all shrink-0 ${
                        isCopied
                          ? 'bg-green-50 text-green-600 border border-green-200'
                          : 'bg-accent-text text-white hover:bg-accent-text/90'
                      }`}
                    >
                      {isCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {isCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-content/30">
                      Expires {new Date(token.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-accent-text hover:text-accent-text/80 flex items-center gap-1 font-medium"
                    >
                      Open scanner
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              );
            })}

            {/* Generate button */}
            <button
              onClick={generateCheckInLink}
              disabled={generatingLink}
              className="w-full py-2.5 px-4 bg-accent-text text-white text-sm font-medium rounded-xl hover:bg-accent-text/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {generatingLink ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <QrCode className="h-4 w-4" />
              )}
              {checkInTokens.length === 0 ? 'Generate Check-in Link' : 'Generate Another Link'}
            </button>
          </div>
        )}
      </div>

      {/* Orders list toggle */}
      {orders.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setShowOrders(!showOrders)}
            className="w-full flex items-center justify-between p-4"
          >
            <span className="text-sm font-semibold text-content">
              Recent Orders ({orders.length})
            </span>
            <Eye className={`h-4 w-4 text-content/40 ${showOrders ? 'text-accent-text' : ''}`} />
          </button>

          {showOrders && (
            <div className="border-t border-gray-100 divide-y divide-gray-50">
              {orders.slice(0, 20).map(order => (
                <div key={order.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-content truncate">{order.buyer_name || order.buyer_email}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-content/40">
                        {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {order.items && (
                        <span className="text-[10px] text-content/30">
                          {order.items.reduce((s, i) => s + i.quantity, 0)} tickets
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-bold text-content">
                      {sym}{(order.total_amount / 100).toFixed(2)}
                    </span>
                    {order.status === 'paid' ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : order.status === 'failed' ? (
                      <XCircle className="h-4 w-4 text-red-500" />
                    ) : (
                      <Clock className="h-4 w-4 text-amber-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {orders.length === 0 && (
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Ticket className="h-5 w-5 text-content/30" />
          </div>
          <p className="text-sm text-content/40">No ticket sales yet</p>
        </div>
      )}
    </div>
  );
}
